<template>
  <div>
    <div
      v-if="actionsAsDropdown"
      class="dropdown"
    >
      <!-- Displays "More/details" dropdown with actions -->
      <button
        class="btn btn-sm btn-success dropdown-toggle"
        type="button"
        data-bs-toggle="dropdown"
      >
        {{ actionDropdownTitle }}
      </button>
      <div class="dropdown-menu">
        <template
          v-for="(action, actionIdx) in itemActionsList"
          :key="actionIdx"
        >
          <a
            v-if="action.type == 'url'"
            :href="fActionUrl(action, item)"
            class="dropdown-item"
          >
            {{ action.title }}
          </a>
          <div
            v-else-if="action.type == 'separator'"
            class="dropdown-divider"
          />
          <a
            v-else
            href="#"
            class="dropdown-item"
            @click.prevent="triggerItemAction(action)"
          >
            {{ action.title }}
          </a>
        </template>
      </div>
    </div>
    <div
      v-else-if="actionsAsSplitButton"
      class="btn-group"
    >
      <a
        v-if="defaultAction && defaultActionIsLink"
        :href="fActionUrl(defaultAction, item)"
        class="btn btn-sm btn-success"
      >
        {{ defaultAction.title }}
      </a>
      <a
        v-else-if="defaultAction && defaultActionIsAction"
        href="#"
        class="btn btn-sm btn-success"
        @click.prevent="triggerItemAction(defaultAction || defaultActionName)"
      >
        {{ defaultAction.title }}
      </a>
      <button
        type="button"
        class="btn btn-sm btn-success dropdown-toggle dropdown-toggle-split"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        data-bs-boundary="viewport"
        data-bs-popper-config='{"strategy":"fixed"}'
      >
        <span class="visually-hidden">Toggle Dropdown</span>
      </button>
      <div class="dropdown-menu">
        <template
          v-for="(action, actionIdx) in itemActionsList.slice(1)"
          :key="actionIdx"
        >
          <a
            v-if="isPostLinkAction(action)"
            :href="fActionUrl(action, item)"
            class="dropdown-item"
            :data-method="action.method"
            :data-confirm="action.confirm"
          >
            {{ action.title }}
          </a>
          <a
            v-else-if="action.type == 'url'"
            :href="fActionUrl(action, item)"
            class="dropdown-item"
          >
            {{ action.title }}
          </a>
          <div
            v-else-if="action.type == 'separator'"
            class="dropdown-divider"
          />
          <a
            v-else
            href="#"
            class="dropdown-item"
            @click.prevent="triggerItemAction(action)"
          >
            {{ action.title }}
          </a>
        </template>
      </div>
    </div>
    <a
      v-else-if="defaultAction && defaultActionIsLink"
      :href="fActionUrl(defaultAction, item)"
      class="btn btn-sm btn-success"
    >
      {{ defaultAction.title }}
    </a>
    <a
      v-else-if="defaultAction && defaultActionIsAction"
      href="#"
      class="btn btn-sm btn-success"
      @click.prevent="triggerItemAction(defaultAction || defaultActionName)"
    >
      {{ defaultAction.title }}
    </a>
  </div>
</template>
<script setup lang="ts">

import {computed} from 'vue'
import type {GridItem, ItemAction} from './types'
import {isNonEmptyString} from '../js-utils'

const props = defineProps({
  item: {default: null, type: Object as () => GridItem | null},
  editActionAsLink: {default: true, type: Boolean},
  viewActionAsLink: {default: false, type: Boolean},
})

const emit = defineEmits<{
  (e: 'item-action', item: GridItem | null, action: ItemAction | string): void
}>()

const itemActionsList = computed((): ItemAction[] => {
  let actionItems: ItemAction[] = []
  if (props.item) {
    if (props.item.meta && Array.isArray(props.item.meta.actions)) {
      actionItems = props.item.meta.actions.slice()
    }

    if (actionItems.length == 0) {
      // construct a default item. If no actionItems are given, but item has edit/view link, display single button
      let defaultItem: ItemAction | null = null
      if (displayAsEditLink.value) {
        defaultItem = {
          type: 'url',
          url: editUrl.value || undefined,
          title: 'Edit',
          name: 'edit'
        }
      } else if (displayAsEditAction.value) {
        defaultItem = {
          type: 'action',
          title: 'Edit',
          name: 'edit'
        }
      } else if (displayAsViewLink.value) {
        defaultItem = {
          type: 'url',
          url: showUrl.value || undefined,
          title: 'View',
          name: 'view'
        }
      } else if (displayAsViewAction.value) {
        defaultItem = {
          type: 'action',
          title: 'View',
          name: 'view'
        }
      }
      if (defaultItem) {
        actionItems.splice(0, 0, defaultItem)
      }
    }
  }

  // a special handling of separator items when displayed as a split button
  // First item from list is used as a button title, other items are put in a menu
  // For this to work properly:
  //   1. all the separator items from the start of menu are removed
  //   2. if second item in the action list is separator, it's removed, because popup menu should start with an actual item

  // if (this.actionsAsDropdown) { // can't use it, because actionsAsDropdown depends on itemActionsList
  if (isNonEmptyString(actionTitle.value) && actionItems.length > 0) {
    // remove first separator item
    while (actionItems.length > 0 && actionItems[0].type == 'separator') {
      actionItems.splice(0, 1)
    }

    if (isNonEmptyString(actionTitle.value) && actionItems.length > 1) {
      // remove second item if it's a separator
      while (actionItems.length > 1 && actionItems[1].type == 'separator') {
        actionItems.splice(1, 1)
      }
    }
  }

  return actionItems
})

const actionTitle = computed((): string | null => {
  if (isNonEmptyString(props.item?.meta?.actionTitle)) {
    return props.item.meta.actionTitle
  }

  return null
})

const actionDropdownTitle = computed((): string => {
  return actionTitle.value || 'More'
})

/**
 * Returns action for the button
 */
const defaultAction = computed((): ItemAction | null => {
  if (itemActionsList.value.length > 0) {
    return itemActionsList.value[0]
  }

  return null
})

const defaultActionName = computed((): string => {
  return defaultAction.value?.name || ''
})

const defaultActionIsLink = computed((): boolean => {
  return defaultAction.value?.type == 'url' || false
})

const defaultActionIsAction = computed((): boolean => {
  return defaultAction.value?.type == 'action' || false
})

/**
 * @fixme remove
 */
const editUrl = computed((): string | null => {
  if (isNonEmptyString(props.item?.links?.edit)) {
    return props.item.links.edit
  }

  return null
})

/**
 * @fixme remove
 */
const showUrl = computed((): string | null => {
  if (isNonEmptyString(props.item?.links?.view)) {
    return props.item.links.view
  }

  return null
})

/**
 * @fixme remove
 */
const hasEditUrl = computed((): boolean => {
  return isNonEmptyString(editUrl.value)
})

/**
 * @fixme remove
 */
const hasShowUrl = computed((): boolean => {
  return isNonEmptyString(showUrl.value)
})

/**
 * Determines if only dropdown menu (More) or button (edit/show) with dropdown menu is displayed
 */
const actionsAsDropdown = computed((): boolean => {
  return isNonEmptyString(actionTitle.value) && itemActionsList.value.length > 0
})

const actionsAsSplitButton = computed((): boolean => {
  return actionTitle.value == null && itemActionsList.value.length > 1
})

/**
 * @fixme remove
 */
const displayAsViewLink = computed((): boolean => {
  return hasShowUrl.value && props.viewActionAsLink
})

/**
 * @fixme remove
 */
const displayAsViewAction = computed((): boolean => {
  return hasShowUrl.value && !props.viewActionAsLink
})

/**
 * @fixme remove
 */
const displayAsEditLink = computed((): boolean => {
  return hasEditUrl.value && props.editActionAsLink
})

/**
 * @fixme remove
 */
const displayAsEditAction = computed((): boolean => {
  return hasEditUrl.value && !props.editActionAsLink
})

const triggerItemAction = (action: ItemAction | string): void => {
  emit('item-action', props.item, action)
}

const isPostLinkAction = (action: ItemAction): boolean => {
  return action.type == 'url' && isNonEmptyString(action.method) && isNonEmptyString(action.confirm)
}

const fActionUrl = (action: ItemAction, item: GridItem | null): string => {
  if (isNonEmptyString(action.link)) {
    if (item && item.links) {
      const url = item.links[action.link]
      if (isNonEmptyString(url)) {
        return url
      }
    }
  }

  if (isNonEmptyString(action.url)) {
    return action.url
  }

  return '#'
}

</script>
